import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { strings } from '/imports/strings';
import { fileHref } from '/imports/api/files';
import { css } from '/imports/ui/css';
import { formatDateTime, randomString } from '/imports/utils';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Button } from '/imports/ui/Button';
import { Page } from '/imports/ui/Page';
import { modelClass, SupportTicket } from '/imports/api/supportTickets';
import { TextArea } from '/imports/ui/TextArea';
import { PageHeading } from '/imports/ui/PageHeading';
import { useFind, useMethod, useSubscription, useUser } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { SupportTicketMessages, modelClass as supportTicketMessageClass } from '/imports/api/supportTicketMessages';
import { PageBody } from '/imports/ui/PageBody';
import { FilesInput } from '/imports/ui/FilesInput';

const SupportTicketPageStyled = styled(Page)`
  .files-input {
    .target {
      padding: 0;
    }
    tbody {
      display: flex;
      gap: 1rem;
    }
  }
  .field-group {
    margin-bottom: 1rem;
  }
  &.not-new {
    .page-heading {
      padding-bottom: 0;
      h1 {
        line-height: 2.5rem;
        span {
          font-size: 1.5rem;
          font-weight: normal;
        }
      }
    }
    .description, .message {
      border: ${css.border};
      border-radius: ${css.borderRadius};
      padding: 1rem;
    }
    .new-message {
      flex: initial;
      .page-heading {
        display: none;
      }
      .page-body {
        padding: 0;
      }
    }
    .messages {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
    .message {
      .headers {
        font-size: 0.875rem;
        color: ${css.grey4};
        text-align: right;
      }
    }
    .files {
      margin-top: 0.5rem;
      border-top: ${css.border};
      a {
        margin-left: 1rem;
      }
    }
    .reply-button {
      margin-bottom: 1rem;
    }
  }
  .related-tickets {
    h3 {
      color: red;
      margin-top: 0;
    }
    table {
      margin-bottom: 2rem;
      td {
        padding: 1rem;
        &:first-child {
          padding-left: 0;
        }
      }
    }
  }
`;

/** Support ticket page: creates a new ticket, or views an existing one with its message thread and reply form. */
export const SupportTicketPage = () => {
  const user = useUser();
  const userId = user?._id;
  const { _id } = useParams<any>();
  const isNew = _id === 'new';
  const defaultUpdate: Partial<SupportTicket> = { _id, active: true };
  const [update, setUpdate] = useState<Partial<SupportTicket>>(defaultUpdate);
  const documentRef = useRef<SupportTicket>(undefined);
  const loadingMessages = isNew ? false : useSubscription(Publications.listSupportTicketMessages, { _id });
  const messages = useFind(() => SupportTicketMessages.find({ supportTicketId: _id }, { sort: { updatedAt: -1 } }), [loadingMessages]);
  const [ticket, loadingTicket] = isNew ? [undefined, false] : useMethod(modelClass.getMethod, _id as string, [_id]);
  const [renderKey, setRenderKey] = useState(randomString());
  const [replying, setReplying] = useState(false);
  const loading = loadingMessages || loadingTicket;
  if (loading) return <LoadingFlex />;
  if (isNew) {
    return <ModelFormPage
      modelClass={modelClass}
      styled={SupportTicketPageStyled}
      documentRef={documentRef}
      defaultUpdate={defaultUpdate}
      update={update}
      setUpdate={setUpdate}
      submitButtonText={strings.Submit()}
      formSpec={isNew ? [
        [{ field: 'subject' }],
        [{ field: 'description', input: { type: TextArea } }],
        [{ field: 'files', input: { type: FilesInput } }],
      ] : []}
    />;
  }
  return (
    <SupportTicketPageStyled className="not-new">
      <PageHeading
        left={<h1>{strings.SupportCase()}: {ticket?.caseNumber}<br /><span>{ticket?.subject}</span></h1>}
      />
      <PageBody>
        {
          replying ?
            <ModelFormPage
              key={renderKey}
              className="new-message"
              modelClass={supportTicketMessageClass}
              defaultUpdate={{ _id: 'new', active: true, supportTicketId: _id }}
              formSpec={[
                [{ field: 'content', input: { type: TextArea }, label: strings.Message() }],
                [{ field: 'files', input: { type: FilesInput } }],
              ]}
              redirect={null}
              onSave={() => setRenderKey(randomString())}
              submitButtonText={strings.Submit()}
            />
            :
            <Button className="reply-button primary" onClick={() => setReplying(true)}>{strings.Reply()}</Button>
        }
        <div className="messages">
          {
            messages.map((m) => (
              <div key={m._id} className="message">
                <div className="headers">{m.createdByUserId === userId ? strings.You() : m.createdByName} @ {formatDateTime(m.updatedAt || new Date())}</div>
                <div className="content">{m.content}</div>
                {
                  m.files?.length ?
                    <div className="files">
                      {strings.Files()}: {m.files.map((f) => <a key={f.md5} href={fileHref(f)} target="_blank" rel="noreferrer">{f.name}</a>)}
                    </div>
                    :
                    null
                }
              </div>
            ))
          }
        </div>
        <div className="description">
          {ticket?.description}
          {
            ticket?.files?.length ?
              <div className="files">
                {strings.Files()}: {ticket.files.map((f) => <a key={f.md5} href={fileHref(f)} target="_blank" rel="noreferrer">{f.name}</a>)}
              </div>
              :
              null
          }
        </div>
      </PageBody>
    </SupportTicketPageStyled>
  );
};